import React, { useEffect, useRef, useState, RefObject } from 'react';
import { Media } from '../../types/media';
import { getFileName, getFileSize, isDocx, isImage, isVideo, isPdf, isXls } from "../../helpers/media";
import Save from "../../assets/save.svg";
import PdfIcon from '../../assets/pdf-icon.svg';
import FileIcon from '../../assets/icons8-file.svg';
import ExcelIcon from '../../assets/icons8-excel.svg';
import WordIcon from '../../assets/icons8-microsoft-word.svg';

import DocumentImg from "../../assets/document.svg";
import DeleteIcon from "../../assets/delete.svg";
import { TravelClaimFileInList } from "../../types/travelClaims";
import AttachmentComponentProps from "./AttachmentComponentProps";
import AttachmentModalProps from "./AttachmentModalProps";
import { RenderDocx } from "./RenderDocx";
import { RenderExcel } from "./RenderExcel";
import { RenderImage } from "./RenderImage";
import { RenderVideo } from './RenderVideo';
import { RenderDocument } from './RenderDocument';
import { RenderPdf } from './RenderPdf';
import { PdfComponent } from "./PdfComponent";

interface AttachmentProps {
    media?: Media
    file_obj?: TravelClaimFileInList
    getFile?: (id: string) => Media | undefined;
}

const ExcelComponent: React.FC<AttachmentComponentProps> = ({ media, file_obj, onClick }) => {
    return <div
        onClick={onClick}
        className="min-w-24 max-w-72 cursor-pointer flex flex-row justify-start items-center"
    >
        <div className="w-14 h-14 flex-[0_0_auto] flex flex-row justify-start items-center">
            <img
                alt={media?.original_name || file_obj?.media_info?.original_name}
                src={media?.base64_preview ? `data:image/png;base64,${media.base64_preview}` : file_obj?.media_info.base64_preview ? `data:image/png;base64,${file_obj.media_info.base64_preview}` : ExcelIcon}
                className='
                    contain-content
                    font-medium
                    text-blue-400
                    dark:text-blue-500
                    hover:underline
                '
            />
        </div>
        <div
            className="
                text-gray-600
                text-[0.8rem] p-1
                overflow-hidden
                whitespace-pre-wrap
                break-word-only-long
            "
        >
            {media && getFileName(media.original_name, 50)} {media && getFileSize(media.size, 6, 2)}
            {file_obj && getFileName(file_obj.media_info.original_name, 50)} {file_obj && getFileSize(file_obj.media_info.size, 6, 2)}
        </div>
    </div>
}

const DocComponent: React.FC<AttachmentComponentProps> = ({ media, file_obj, onClick }) => {
    return <div
        onClick={onClick}
        className="
                    min-w-24
                    max-w-72
                    cursor-pointer
                    flex flex-row
                    justify-start
                    items-center
                    rounded-md
                    overflow-hidden
            ">
        <div className="w-14 h-14 flex-[0_0_auto] flex flex-row justify-start items-center overflow-hidden">
            <img
                alt={media?.original_name || file_obj?.media_info?.original_name}
                src={media?.base64_preview ? `data:image/png;base64,${media.base64_preview}` : file_obj?.media_info.base64_preview ? `data:image/png;base64,${file_obj.media_info.base64_preview}` : WordIcon}
                className='
                        w-full
                        object-cover
                        font-medium
                        text-blue-400
                        dark:text-blue-500
                        hover:underline'
            />
        </div>
        <div className="
        text-gray-600
        text-[0.8rem] p-1 overflow-hidden
        whitespace-pre-wrap
        break-word-only-long
        ">
            {media && getFileName(media.original_name, 50)} {media && getFileSize(media.size, 6, 2)}
            {file_obj && getFileName(file_obj.media_info.original_name, 50)} {file_obj && getFileSize(file_obj.media_info.size, 6, 2)}
        </div>
    </div>
}

const VideoComponent: React.FC<AttachmentComponentProps> = ({ media, file_obj, onClick }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    const url = media?.file || file_obj?.media_info?.file || '';
    // бэкенд превью для видео не делает поэтому первый кадр берём фрагментом t
    // без него в img уезжал сам mp4 и плитка была пустой
    const posterless = url && !url.includes('#') ? `${url}#t=0.1` : url;

    useEffect(() => {
        // auto scroll to msg bottom
        if (videoRef.current) {
            videoRef.current.onloadeddata = () => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            };
        }
    }, [media, file_obj]);

    return (
        <div className="min-w-24 max-w-72 cursor-pointer bg-blue-200 rounded-md overflow-hidden">
            <div className="relative" onClick={onClick}>
                <video
                    ref={videoRef}
                    src={posterless}
                    poster={media?.preview?.file}
                    preload="metadata"
                    muted
                    playsInline
                    className="w-full contain-content"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <svg viewBox="0 0 64 64" className="w-12 h-12 opacity-80" aria-hidden="true">
                        <circle cx="32" cy="32" r="30" fill="rgba(0,0,0,0.55)" />
                        <path d="M25 20 L47 32 L25 44 Z" fill="#fff" />
                    </svg>
                </div>
            </div>
            <div
                className="text-gray-600 text-[0.8rem] p-1 overflow-hidden whitespace-pre-wrap break-word-only-long"
                ref={messagesEndRef}
            >
                {media && getFileName(media.original_name, 50)} {media && getFileSize(media.size, 6, 2)}
                {file_obj && getFileName(file_obj.media_info.original_name, 50)} {file_obj && getFileSize(file_obj.media_info.size, 6, 2)}
            </div>
        </div>
    );
};

const ImageComponent: React.FC<AttachmentComponentProps> = ({ media, file_obj, onClick }) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // auto scroll to msg bottom
        if (imgRef.current) {
            imgRef.current.onload = () => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            };
        }
    }, [media, file_obj]);

    return (
        <div className="min-w-24 max-w-72 cursor-pointer bg-blue-200 rounded-md overflow-hidden">
            <img
                ref={imgRef}
                alt={media?.original_name || file_obj?.media_info?.original_name}
                src={media?.preview?.file || media?.file || file_obj?.media_info?.file}
                onClick={onClick}
                className='font-medium contain-content text-blue-400 dark:text-blue-500 hover:underline'
            />
            <div
                className="text-gray-600 text-[0.8rem] p-1 overflow-hidden whitespace-pre-wrap break-word-only-long"
                ref={messagesEndRef}
            >
                {media && getFileName(media.original_name, 50)} {media && getFileSize(media.size, 6, 2)}
                {file_obj && getFileName(file_obj.media_info.original_name, 50)} {file_obj && getFileSize(file_obj.media_info.size, 6, 2)}
            </div>
        </div>
    );
};


const FileComponent: React.FC<AttachmentComponentProps> = ({ media, file_obj, icon, onClick }) => {
    return <div className="min-w-24 max-w-72">
        <a
            href={media?.file || file_obj?.media_info?.file}
            target={'_blank'}
            className='
                    relative
                    w-full
                    flex
                    bg-blue-200
                    flex-row justify-start pl-0 pr-2 py-1
                    items-center gap-1
                    font-medium
                    text-gray-600
                    dark:text-blue-500
                    rounded-md
                    overflow-hidden
                    text-[0.8rem]
                    cursor-pointer
                    hover:appearance-none
                '
        >
            <div className="w-14 h-14 flex-[0_0_auto] flex flex-row justify-start items-center">
                <img
                    alt={media?.original_name || file_obj?.media_info?.original_name}
                    src={media?.base64_preview ? `data:image/png;base64,${media.base64_preview}` : file_obj?.media_info.base64_preview ? `data:image/png;base64,${file_obj.media_info.base64_preview}` : icon}
                    className='
                            w-full
                            ml-1
                            font-medium
                            contain-content
                            text-blue-400
                            dark:text-blue-500
                            hover:underline'
                />
            </div>
            <div
                className="
                        absolute
                        inset-0 flex
                        items-center
                        justify-center bg-melon-yellow
                        bg-opacity-50 opacity-0
                        hover:opacity-100
                        rounded-md
                        transition-opacity duration-300
                    "
            >
                <img src={Save} alt="download file" />
            </div>
            <div className="
                    overflow-hidden
                    whitespace-pre-wrap
                    break-word-only-long
                ">
                {media && getFileName(media.original_name, 50)} {media && getFileSize(media.size, 6, 2)}
                {file_obj && getFileName(file_obj.media_info.original_name, 50)} {file_obj && getFileSize(file_obj.media_info.size, 6, 2)}
            </div>
        </a>
    </div>
}

export const RenderDownloadFile = ({ media, file_obj, disabled, onRemove, onClick, component }: AttachmentModalProps) => {
    let icon = FileIcon;
    let _file = '';

    if (media) {
        _file = media.file
    }
    if (file_obj) {
        _file = file_obj.media_info.file
    }

    if (isImage(_file)) {
        icon = _file;
    }
    if (isDocx(_file)) {
        icon = WordIcon;
    }
    if (isPdf(_file)) {
        icon = PdfIcon;
    }
    if (isXls(_file)) {
        icon = ExcelIcon;
    }


    return <>
        {media && component({ media, file_obj, icon })}
        {file_obj && component({ file_obj, disabled, onRemove, onClick, icon })}
    </>

}

export const Attachment = ({ media, file_obj, getFile }: AttachmentProps) => {
    let _file = '';

    if (media) {
        _file = media.file
    }
    if (file_obj) {
        _file = file_obj.media_info.file
    }

    if (isImage(_file)) {
        return <RenderImage media={media} component={ImageComponent} />;
    }
    if (isVideo(_file)) {
        return <RenderVideo media={media} component={VideoComponent} />;
    }
    if (isDocx(_file)) {
        return <RenderDocument
            media={media}
            component={DocComponent}
            getFile={(id) => getFile?.(id) ?? ({} as Media)}
        />;
    }
    if (isPdf(_file)) {
        return <RenderPdf media={media} component={PdfComponent} />;
    }
    if (isXls(_file)) {
        return <RenderExcel media={media} component={ExcelComponent} />;
    }
    return <RenderDownloadFile media={media} component={FileComponent} />;
};

export const AttachmentIcon = ({ media, file_obj }: AttachmentProps) => {
    let icon = FileIcon;
    let _file = '';

    if (media) {
        _file = media.file
    }
    if (file_obj) {
        _file = file_obj.media_info.file
    }

    if (isImage(_file)) {
        icon = _file;
    }
    if (isDocx(_file)) {
        icon = WordIcon;
    }
    if (isPdf(_file)) {
        icon = PdfIcon;
    }
    if (isXls(_file)) {
        icon = ExcelIcon;
    }

    return <img src={icon} alt="image" className="rounded-md object-cover w-full h-full" />
};

interface ColleagueAttachmentProps {
    file_obj: TravelClaimFileInList
    disabled?: boolean
    getFile?: (id: string) => Media | undefined;
    handleRemove: (id: string) => void
    handleDownload: (file: TravelClaimFileInList) => void
}

const ColleagueAttachmentComponent: React.FC<AttachmentComponentProps> = ({ file_obj, onClick, onRemove, disabled }) => {
    return <div
        className="
            cursor-pointer
            w-full
            flex flex-row
            justify-between
            items-center
            flex-[0_0_auto]
            pl-3 pr-2 py-2
            rounded-lg
            bg-[#f2f2f2]
        "
    >
        <div
            onClick={onClick}
            className="
            inline-flex
            min-h-12
            w-full
            flex-row items-center
            gap-2
        "
        >
            <img alt="" src={DocumentImg} className="!relative flex-[0_0_auto] !w-6 !h-6" />
            <div
                className="inline-flex justify-center relative flex-col items-start">
                <div
                    className="
                        relative
                        font-manager-acrom-ph-14-medium-acrom
                        font-[number:var(--manager-acrom-ph-14-medium-acrom-font-weight)]
                        text-black text-[length:var(--manager-acrom-ph-14-medium-acrom-font-size)]
                        tracking-[var(--manager-acrom-ph-14-medium-acrom-letter-spacing)]
                        leading-[var(--manager-acrom-ph-14-medium-acrom-line-height)]
                        overflow-hidden
                        whitespace-pre-wrap
                        break-word-only-long
                        [font-style:var(--manager-acrom-ph-14-medium-acrom-font-style)]
                    "
                >
                    {file_obj?.media_info.original_name && getFileName(file_obj?.media_info.original_name, 50)}
                </div>
                <div
                    className="
                        relative w-fit
                        font-manager-acrom-ph-14-medium-acrom
                        font-[number:var(--manager-acrom-ph-14-medium-acrom-font-weight)]
                        text-[#949494] text-[length:var(--manager-acrom-ph-14-medium-acrom-font-size)]
                        tracking-[var(--manager-acrom-ph-14-medium-acrom-letter-spacing)]
                        leading-[var(--manager-acrom-ph-14-medium-acrom-line-height)]
                        whitespace-nowrap
                        [font-style:var(--manager-acrom-ph-14-medium-acrom-font-style)]
                    "
                >
                    {/*{getFileSize(file_obj?.media_info.size)}*/}
                    {file_obj?.media_info.size} MB
                </div>
            </div>
        </div>
        <button
            disabled={disabled}
            onClick={onRemove}
            className="cursor-pointer relative w-6 h-6">
            <img src={DeleteIcon} alt="" />
        </button>
    </div>
}

export const ColleagueAttachment = ({ handleRemove, file_obj, disabled, getFile }: ColleagueAttachmentProps) => {

    if (isImage(file_obj.media_info.file)) {
        return <RenderImage
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueAttachmentComponent} />;
    }
    if (isVideo(file_obj.media_info.file)) {
        return <RenderVideo
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueAttachmentComponent} />;
    }
    if (isDocx(file_obj.media_info.file)) {
        return <RenderDocument
            file_obj={file_obj}
            disabled={disabled}
            media={file_obj.media_info}
            getFile={(id) => getFile?.(id) ?? ({} as Media)}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueAttachmentComponent} />;
    }
    if (isPdf(file_obj.media_info.file)) {
        return <RenderPdf
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueAttachmentComponent} />;
    }
    if (isXls(file_obj.media_info.file)) {
        return <RenderExcel
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueAttachmentComponent} />;
    }

    return <RenderDownloadFile
        file_obj={file_obj}
        disabled={disabled}
        onRemove={() => handleRemove(file_obj.pk)}
        component={ColleagueAttachmentComponent} />;
}

const ColleagueDocAttachmentComponent: React.FC<AttachmentComponentProps> = ({ file_obj, onClick, onRemove, disabled }) => {
    return <div onClick={onClick} className="cursor-pointer flex flex-row w-full items-center justify-between gap-1">
        <div className="flex flex-col w-full overflow-x-hidden shrink items-start gap-0.5 relative">
            <div
                className="
                    relative
                    w-full
                    text-wrap
                    min-h-9
                    font-manager-acrom-ph-14-medium-acrom
                    font-[number:var(--manager-acrom-ph-14-medium-acrom-font-weight)]
                    text-black text-[length:var(--manager-acrom-ph-14-medium-acrom-font-size)]
                    tracking-[var(--manager-acrom-ph-14-medium-acrom-letter-spacing)]
                    leading-[var(--manager-acrom-ph-14-medium-acrom-line-height)]
                    overflow-hidden
                    whitespace-pre-wrap
                    break-word-only-long
                    [font-style:var(--manager-acrom-ph-14-medium-acrom-font-style)]
                "
            >
                {file_obj?.media_info.original_name && getFileName(file_obj?.media_info.original_name, 50)}
            </div>
            <div
                className="
                    relative w-full
                    font-manager-acrom-ph-14-medium-acrom
                    font-[number:var(--manager-acrom-ph-14-medium-acrom-font-weight)]
                    text-[#949494] text-[length:var(--manager-acrom-ph-14-medium-acrom-font-size)]
                    tracking-[var(--manager-acrom-ph-14-medium-acrom-letter-spacing)]
                    leading-[var(--manager-acrom-ph-14-medium-acrom-line-height)]
                    [font-style:var(--manager-acrom-ph-14-medium-acrom-font-style)]
                "
            >
                {file_obj?.media_info.size}MB
            </div>
        </div>
    </div>
}

export const ColleagueDocAttachment = ({ handleRemove, handleDownload, file_obj, disabled, getFile }: ColleagueAttachmentProps) => {
    if (isImage(file_obj.media_info.file)) {
        return <RenderImage
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueDocAttachmentComponent} />;
    }
    if (isVideo(file_obj.media_info.file)) {
        return <RenderVideo
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueDocAttachmentComponent} />;
    }
    if (isDocx(file_obj.media_info.file)) {
        return <RenderDocument
            file_obj={file_obj}
            disabled={disabled}
            media={file_obj.media_info}
            getFile={(id) => getFile?.(id) ?? ({} as Media)}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueDocAttachmentComponent} />;
    }
    if (isPdf(file_obj.media_info.file)) {
        return <RenderPdf
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueDocAttachmentComponent} />;
    }
    if (isXls(file_obj.media_info.file)) {
        return <RenderExcel
            file_obj={file_obj}
            disabled={disabled}
            onRemove={() => handleRemove(file_obj.pk)}
            component={ColleagueDocAttachmentComponent} />;
    }

    return <RenderDownloadFile
        file_obj={file_obj}
        disabled={disabled}
        onRemove={() => handleRemove(file_obj.pk)}
        onClick={() => handleDownload(file_obj)}
        component={ColleagueDocAttachmentComponent} />;
}


